import { Box, Skeleton, Stack, Typography } from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { Sstv2MigrationMetricCounts } from "./sstv2MigrationTypes"

const cardSx = {
  bgcolor: "white",
  border: "1px solid",
  borderColor: "rgba(0, 0, 0, 0.08)",
  borderRadius: 2,
  boxShadow: "0 1px 2px rgba(35, 66, 133, 0.04)",
}

const eyebrowSx = {
  fontSize: "0.72rem",
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "text.secondary",
}

/**
 * One number. `hint` carries the denominator or qualifier inline (e.g. "of 12
 * lockers") so the number never needs a legend to be read correctly.
 */
const MetricCard: FC<{
  labelKey: string
  value: number
  dotColor: string
  loading: boolean
  hint?: string
  /** Draws attention only when non-zero — used for the stalled count. */
  alertWhenNonZero?: boolean
}> = ({ labelKey, value, dotColor, loading, hint, alertWhenNonZero }) => {
  const { t } = useTranslation()
  const alert = !!alertWhenNonZero && value > 0
  return (
    <Box
      sx={{
        ...cardSx,
        p: { xs: 2, md: 2.5 },
        flex: 1,
        minWidth: 150,
        ...(alert && {
          borderColor: "error.main",
          bgcolor: "rgba(211, 47, 47, 0.04)",
        }),
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: alert ? "error.main" : dotColor,
          }}
        />
        <Typography sx={eyebrowSx}>{t(labelKey)}</Typography>
      </Stack>
      <Typography
        variant="h4"
        sx={{ fontWeight: 700, color: alert ? "error.main" : "text.primary" }}
      >
        {loading ? <Skeleton width={60} /> : value}
      </Typography>
      {hint && !loading && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </Box>
  )
}

/**
 * Header numbers for the migration lifecycle. Five cards, each answering a
 * question an operator actually asks; no percentage bars (see
 * `Sstv2MigrationMetricCounts` for why ratios over attempt-rows were dropped).
 */
export const Sstv2MigrationMetrics: FC<{
  counts: Sstv2MigrationMetricCounts
  loading: boolean
}> = ({ counts, loading }) => {
  const { t } = useTranslation()
  return (
    <Stack data-ui="UI-SL2W-253"
      direction="row"
      spacing={2.5}
      flexWrap="wrap"
      rowGap={2.5}
      sx={{ mb: 2.5 }}
    >
      <MetricCard
        labelKey="migrationJobsInFlight"
        value={counts.inFlight}
        dotColor="info.main"
        loading={loading}
        hint={t("migrationOfNJobs", { count: counts.jobs })}
      />
      <MetricCard
        labelKey="migrationStalledJobs"
        value={counts.stalled}
        dotColor="grey.500"
        loading={loading}
        alertWhenNonZero
      />
      <MetricCard
        labelKey="migrationLockersLive"
        value={counts.lockersLive}
        dotColor="primary.main"
        loading={loading}
        hint={t("migrationOfNLockers", { count: counts.lockers })}
      />
      <MetricCard
        labelKey="migrationJobsCompleted"
        value={counts.completed}
        dotColor="success.main"
        loading={loading}
      />
      <MetricCard
        labelKey="migrationJobsRolledBack"
        value={counts.rolledBack}
        dotColor="error.main"
        loading={loading}
      />
    </Stack>
  )
}

export default Sstv2MigrationMetrics
